import { HttpClient } from '@angular/common/http';
import { Injectable, Injector } from '@angular/core';
import { TokenSync } from '@zero/shared/util-models';
import { environment } from 'environments/environment';
import { LOCAL_STORAGE, StorageService } from 'ngx-webstorage-service';
import { BehaviorSubject, Observable, Subject, firstValueFrom, of } from 'rxjs';
import { catchError, debounceTime, map } from 'rxjs/operators';
import { JwtTokenLoggerHelper } from '../authentication/jwt-token-logger.helper';
import { CrossTabsEventsService } from '../dom/cross-tabs-events.service';

@Injectable()
export class ZeroStorage {
  private static _KEY_PREFIX = 'CognitoIdentityServiceProvider';
  public static KEY_EVENTID = 'activeSession';

  #dataMemory = {};

  #initialized = false;
  #http: HttpClient = null;
  #localStorage: StorageService = null;

  readonly #cookieSaved$: Subject<boolean>;
  readonly #triggerSync$ = new Subject<void>();
  #crossTabsEventsService: CrossTabsEventsService;

  constructor(private injector: Injector) {
    this.#cookieSaved$ = new BehaviorSubject(false);
    this.#triggerSync$.pipe(debounceTime(100)).subscribe(() => this.sync());
  }

  #init(): void {
    // TODO Forward Ref
    if (this.#initialized) {
      return;
    }
    this.#localStorage = this.injector.get(LOCAL_STORAGE);
    this.#http = this.injector.get(HttpClient);
    this.#crossTabsEventsService = this.injector.get(CrossTabsEventsService);
    this.listeningOnStorageEvents();
    this.#initialized = true;
  }

  /**
   * This is used to set a specific item in storage
   * PR: Using async / await because we have to block any other operation until the cookie is there.
   * When I did not, the following happened: Amplify goes on without cookie, triggers authState changed and then
   * our application does the next server call (loading data) while cookie is not yet set -> Authentication failure
   */
  public setItem(key: string, value): string {
    this.#init();
    if (key.endsWith('.LastAuthUser')) {
      this.#localStorage.set(key, value);
      return value;
    }
    if (key.endsWith('.clockDrift')) {
      this.#dataMemory[key] = value;
      return value;
    }

    if (key.endsWith('.accessToken') || key.endsWith('.userData')) {
      // die werte werden encrypted
      this.#dataMemory[key] = value;
      return value;
    }

    if (key && (key.endsWith('.refreshToken') || key.endsWith('.idToken'))) {
      // die landen im dataMemory & in sicherem Cookie
      if (this.#dataMemory[key] === value) {
        return value; // nothing to do
      }

      if (key.endsWith('.idToken')) {
        const eventId = JwtTokenLoggerHelper.getEventId(value);
        this.#localStorage.set(ZeroStorage.KEY_EVENTID, eventId);
        this.#cookieSaved$.next(false);
      }

      this.#dataMemory[key] = value;

      if (!environment.useLocalStorageToStoreTokens) {
        this.#http
          .post(`${environment.apihost}/api/auth/save`, value)
          .pipe(
            catchError(() => {
              if (key.endsWith('.idToken')) {
                this.#cookieSaved$.next(true);
              }
              return of(null);
            }),
          )
          .subscribe(() => {
            if (key.endsWith('.idToken')) {
              this.#cookieSaved$.next(true);
            }
          });
      } else {
        this.#localStorage.set(key, value);
        this.#cookieSaved$.next(true);
      }
    }

    this.#dataMemory[key] = value;
    return this.#dataMemory[key];
  }

  /**
   * This is used to get a specific key from storage
   * CANNOT USE PROMISES HERE; MUST RETURN A STRING IMMEDIATELY!
   */
  public getItem(key: string): string {
    this.#init();
    if (key.endsWith('.LastAuthUser')) {
      return this.#localStorage.get(key);
    }
    const result = this.#dataMemory[key];

    if (!result) {
      if (environment.useLocalStorageToStoreTokens) {
        return this.#localStorage.get(key);
      }
    }
    return result;
  }

  /**
   * This is used to remove an item from storage
   */
  public removeItem(key) {
    this.#init();
    this.#localStorage.remove(key);
    delete this.#dataMemory[key];
  }

  /**
   * This is used to clear the storage
   */
  public clear() {
    this.#init();
    this.#dataMemory = {};
    this.#localStorage.clear();
    return this.#dataMemory;
  }

  //
  /**
   * Will sync the MemoryStorage data from AsyncStorage to storageWindow MemoryStorage
   * GB: At this point we try to load tokens from server
   */
  public sync(): Promise<void> {
    this.#init();

    if (!environment.useLocalStorageToStoreTokens) {
      return firstValueFrom(
        this.#http
          .get<TokenSync>(`${environment.apihost}/api/auth/sync`, {
            withCredentials: true,
          })
          .pipe(
            catchError(() => of(undefined)),
            map((tokenResponse) => {
              if (tokenResponse) {
                const keyRefreshToken = `${ZeroStorage._KEY_PREFIX}.${tokenResponse.aud}.${tokenResponse.username}.refreshToken`;
                const keyIdToken = `${ZeroStorage._KEY_PREFIX}.${tokenResponse.aud}.${tokenResponse.username}.idToken`;

                if (tokenResponse.idToken) {
                  this.#dataMemory[keyIdToken] = tokenResponse.idToken;
                  this.#dataMemory[keyRefreshToken] = tokenResponse.refreshToken;
                  this.#cookieSaved$.next(true);
                }
              }
            }),
          ),
      );
    } else {
      return Promise.resolve();
    }
  }

  public getCookieSaved$(): Observable<boolean> {
    return this.#cookieSaved$;
  }

  public listeningOnStorageEvents(): void {
    this.#crossTabsEventsService.getEventIdChange$().subscribe(() => {
      this.#triggerSync$.next();
    });
  }
}
